<template>
  <v-dialog
    :value="isOpen"
    max-width="630px"
    :persistent="mode === 'edit'"
    @input="onDialogInput"
  >
    <v-card class="comment-card" :style="cardStyle">
      <v-card-title class="comment-header py-3">
        <span class="comment-title" :title="titleText">{{ titleText }}</span>
        <v-spacer />
        <v-btn icon small aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="comment-body py-4">
        <div v-if="loading" class="comment-loading text-center">Loading…</div>

        <!-- EDIT MODE: the user's own (private) comment -->
        <template v-else-if="mode === 'edit'">
          <v-textarea
            ref="textarea"
            v-model="draft"
            autofocus
            auto-grow
            rows="3"
            counter="4000"
            maxlength="4000"
            hide-details="auto"
            placeholder="Add a private comment for this track…"
          />
        </template>

        <!-- THREAD MODE: everyone's comments, anonymous + read-only -->
        <template v-else>
          <div v-if="thread.length === 0" class="comment-empty text-center">
            No comments yet.
          </div>
          <div v-for="(c, i) in thread" :key="i" class="comment-bubble">
            <div class="comment-bubble-body">{{ c.body }}</div>
            <div class="comment-bubble-time">last edited {{ formatTime(c.updated_at) }}</div>
          </div>
        </template>
      </v-card-text>

      <template v-if="mode === 'edit'">
        <v-divider />
        <v-card-actions class="py-3">
          <v-spacer />
          <v-btn color="secondary" :loading="saving" @click="save">OK</v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'CommentDialog',

  data() {
    return {
      draft: '',
      // visual-viewport tracking (mobile keyboard handling)
      vvHeight: null,
      vvOffsetTop: 0,
    };
  },

  computed: {
    ...mapGetters({
      isOpen: 'comments/isOpen',
      mode: 'comments/mode',
      recordingId: 'comments/recordingId',
      myComment: 'comments/myComment',
      thread: 'comments/thread',
      loading: 'comments/loading',
      saving: 'comments/saving',
      recordings: 'playlist/sortedRecordings',
    }),

    // The track this dialog is about (for the title).
    trackName() {
      const rec = (this.recordings || []).find(r => r.id === this.recordingId);
      return rec ? rec.name : '';
    },

    // Title: the track name when adding/editing your comment, else "Comments" for the thread.
    titleText() {
      if (this.mode === 'edit') return this.trackName || 'Your comment';
      return 'Comments';
    },

    // True only on a phone, in edit mode, when the on-screen keyboard has shrunk the
    // visible viewport. The 120px threshold avoids false positives from the URL bar.
    keyboardActive() {
      return (
        this.isOpen &&
        this.mode === 'edit' &&
        this.$vuetify.breakpoint.xs &&
        this.vvHeight != null &&
        window.innerHeight - this.vvHeight > 120
      );
    },

    // When the keyboard is up, pin the card into the visible band above it and cap its
    // height so the textarea + OK button are never covered (body scrolls if needed).
    cardStyle() {
      if (!this.keyboardActive) return {};
      return {
        transform: `translateY(${this.vvOffsetTop}px)`,
        maxHeight: `${Math.max(180, this.vvHeight - 16)}px`,
      };
    },
  },

  watch: {
    isOpen(open) {
      // While open, a body class scopes the z-index fix in the global style block below so
      // this dialog's scrim + card sit above the fixed app chrome (player z-index 999, headers
      // 10000) instead of letting them poke through the grey overlay.
      document.body.classList.toggle('comment-dialog-open', open);
      if (open) {
        if (this.mode === 'edit') {
          // seed the textarea from the user's existing comment (blank if none)
          this.draft = (this.myComment && this.myComment.body) || '';
          this.$nextTick(this.focusTextarea);
        }
        this.$nextTick(this.syncViewport);
      }
    },
    // the comment arrives asynchronously after the dialog opens
    myComment(c) {
      if (this.isOpen && this.mode === 'edit') {
        this.draft = (c && c.body) || '';
      }
    },
  },

  mounted() {
    this._vv = window.visualViewport || null;
    if (this._vv) {
      this._onViewport = () => {
        this.vvHeight = this._vv.height;
        this.vvOffsetTop = this._vv.offsetTop || 0;
      };
      this._vv.addEventListener('resize', this._onViewport);
      this._vv.addEventListener('scroll', this._onViewport);
    }
  },

  beforeDestroy() {
    document.body.classList.remove('comment-dialog-open');
    if (this._vv && this._onViewport) {
      this._vv.removeEventListener('resize', this._onViewport);
      this._vv.removeEventListener('scroll', this._onViewport);
    }
  },

  methods: {
    syncViewport() {
      if (this._vv) {
        this.vvHeight = this._vv.height;
        this.vvOffsetTop = this._vv.offsetTop || 0;
      }
    },

    focusTextarea() {
      const ta = this.$refs.textarea;
      if (ta && typeof ta.focus === 'function') ta.focus();
    },

    save() {
      // a blank/whitespace body deletes the comment (handled server-side); the action
      // updates the icon state and closes the dialog on success.
      this.$store.dispatch('comments/saveMyComment', this.draft);
    },

    close() {
      this.$store.dispatch('comments/close');
    },

    // v-dialog emits input(false) on esc / outside-click (thread mode is non-persistent)
    onDialogInput(value) {
      if (!value) this.close();
    },

    formatTime(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.comment-card {
  display: flex;
  flex-direction: column;
}

.comment-title {
  font-size: 1.1rem;
  font-weight: 500;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.comment-empty {
  opacity: 0.7;
  padding: 20px 0;
}

.comment-bubble {
  border: 1px solid var(--v-tertiary-base);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.comment-bubble:last-child {
  margin-bottom: 0;
}

.comment-bubble-body {
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-bubble-time {
  font-size: 0.72rem;
  opacity: 0.6;
  margin-top: 6px;
}
</style>

<!--
  Non-scoped + scoped-by-body-class: Vuetify generates the dialog's scrim (.v-overlay) and the
  full-viewport wrapper (.v-dialog__content) outside this component's template, and computes their
  z-index (~201/202) unaware of the app's fixed chrome (player z-index 999, headers 10000) — so by
  default the player/header poke through the grey overlay. While this dialog is open the isOpen
  watcher adds `comment-dialog-open` to <body>, scoping these bumps to ONLY this dialog (it is the
  only active overlay then): the scrim is lifted above all app chrome, and the card above the scrim.
-->
<style>
body.comment-dialog-open .v-overlay--active {
  z-index: 999999990 !important;
}
body.comment-dialog-open .v-dialog__content--active {
  z-index: 999999999 !important;
}
@media only screen and (max-width: 599px) {
  /* dock the dialog to the top of the screen so the textarea + OK sit above the keyboard */
  body.comment-dialog-open .v-dialog__content--active {
    align-items: flex-start;
  }
}
</style>
